import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../../hooks/useAppDispatch";
import { GeneralStyles } from "../../../assets/css/general.styles";
import { MenuItem, Checkbox, FormControlLabel, Grid } from "@mui/material";
import GridStyles from "../../../assets/css/grid.styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CustomButton from "../../../components/customButton/customButton";
import CustomTextField from "../../../components/customTextField/customTextField";
import {
  SAVE_LOOKUP,
  VALIDATION_ERROR_FIELDS_REQUIRED_MESSAGE,
  VALIDATION_ERROR_INPUT_ORDER_MESSAGE,
  VALIDATION_ERROR_REQUIRED_MESSAGE,
} from "../../../constants/actions";
import { LookupTouched } from "./lookupTouched";
import CustomSelect from "../../../components/customSelect/customSelect";
import { toast } from "react-toastify";
import {
  getAllLookups,
  getLookupById,
  getLookupValueById,
  saveLookup,
  saveLookupValue,
} from "../../../reducers/lookupReducer";
import { minimumOrder } from "../../../constants/validations";
import { LookupValueModel } from "../../../models/models/lookupValueModel";
import { LookupValueResource } from "../../../models/resource/lookupValueResource";
import { LookupModel } from "../../../models/models/lookupModel";
import { LookupResource } from "../../../models/resource/lookupResource";

const LookupDetailsForm: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { id, parentId, type } = useParams();
  const authToken = useSelector((state: RootState) => state.auth.token);
  const [touched, setTouched] = useState<LookupTouched>({});
  const [isParent, setIsParent] = useState<boolean>(false);
  const parentList = useSelector(
    (state: RootState) => state.lookup.lookupsParentList
  );
  const lookupValue = useSelector(
    (state: RootState) => state.lookup.lookupValue
  );
  const lookup = useSelector((state: RootState) => state.lookup.lookup);
  const [lookupValueData, setLookupValueData] = useState<LookupValueResource>({
    id: 0,
    lookupId: null,
    valueEn: "",
    valueAr: "",
    description: "",
    ordering: 1,
  });
  const [lookupData, setLookupData] = useState<LookupResource>({
    id: 0,
    name: "",
    description: "",
  });

  useEffect(() => {
    if (!authToken) {
      navigate("/Signin");
    }
  }, [authToken]);

  useEffect(() => {
    if (parentId) {
      setLookupValueData((prevData) => ({
        ...prevData,
        lookupId: Number(parentId),
      }));
    }
  }, [parentId]);

  useEffect(() => {
    if (id && type) {
      const itemId = parseInt(id, 10);
      if (type.toLocaleLowerCase() === "parentlookup") {
        dispatch(getLookupById({ id: itemId }));
      } else {
        dispatch(getLookupValueById({ id: itemId }));
      }
    }
  }, [id]);

  useEffect(() => {
    dispatch(getAllLookups());
  }, []);

  useEffect(() => {
    const itemData = lookupValue.data;
    if (itemData && id) {
      const data = itemData as LookupValueModel;
      setLookupValueData({
        id: data.id || 0,
        lookupId: data.lookupId || null,
        valueEn: data.valueEn || "",
        valueAr: data.valueAr || "",
        description: data.description || "",
        ordering: data.ordering || 0,
      });
    }
  }, [lookupValue.data]);

  useEffect(() => {
    const itemData = lookup.data;
    if (itemData && id) {
      const data = itemData as LookupResource;
      setLookupData({
        id: data.id || 0,
        name: data.name || "",
        description: data.description || "",
      });
    }
  }, [lookup.data]);

  const handleLookupValueSave = () => {
    if (
      lookupValueData.lookupId === null ||
      lookupValueData.valueEn.trim() === "" ||
      lookupValueData.valueAr.trim() === "" ||
      lookupValueData.description.trim() === "" ||
      lookupValueData.ordering < minimumOrder ||
      (lookupValueData.ordering !== null &&
        isNaN(Number(lookupValueData.ordering)))
    ) {
      let touchedFields = {
        parent: lookupValueData.lookupId === null,
        valueEn: lookupValueData.valueEn.trim() === "",
        valueAr: lookupValueData.valueAr.trim() === "",
        description: lookupValueData.description.trim() === "",
        ordering:
          lookupValueData.ordering < minimumOrder ||
          (lookupValueData.ordering !== null &&
            isNaN(Number(lookupValueData.ordering))),
      };
      setTouched(touchedFields);
      toast.error(`${VALIDATION_ERROR_FIELDS_REQUIRED_MESSAGE}`);
      return;
    }
    dispatch(saveLookupValue({ data: lookupValueData }))
      .unwrap()
      .then((response: any) => {
        const { success, message } = response;
        if (!success) {
          toast.error(message);
        } else {
          toast.success(`${SAVE_LOOKUP}`);
          navigate("/Lookups");
        }
      })
      .catch((error) => {
        console.error("Failed to save item:", error);
      });
  };

  const handleLookupSave = () => {
    if (lookupData.name.trim() === "" || lookupData.description.trim() === "") {
      let touchedFields = {
        name: lookupData.name.trim() === "",
        description: lookupData.description.trim() === "",
      };
      setTouched(touchedFields);
      toast.error(`${VALIDATION_ERROR_FIELDS_REQUIRED_MESSAGE}`);
      return;
    }

    dispatch(saveLookup({ data: lookupData }))
      .unwrap()
      .then((response) => {
        if (response) {
          toast.success(`${SAVE_LOOKUP}`);
          navigate("/Lookups");
        }
      })
      .catch((error) => {
        console.error("Failed to save item:", error);
      });
  };
  return (
    <form style={GeneralStyles.formContainerStyle}>
      <Grid
        container
        spacing={2}
        sx={{ flexDirection: { xs: "column", sm: "row" } }}
      >
        <Grid item xs={12} sm={6} md={10}>
          <Grid item xs={12} sm={6} md={8}>
            {id ? (
              <h1 style={GridStyles.title}>
                Edit Lookup{" "}
                <b>
                  {type?.toLocaleLowerCase() === "parentlookup"
                    ? lookupData.name
                    : lookupValueData.valueEn}
                </b>
              </h1>
            ) : (
              <h1 style={GridStyles.title}>Add Lookup</h1>
            )}
          </Grid>
          {/* {type === undefined && (
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isParent}
                          style={GeneralStyles.checkBoxStyle}
                            onChange={(e: any) => {  
                            setIsParent(e.target.checked);
                          }}
                          name="isParent"
                        />
                      }
                      label="Is Parent"
                    />
                  </Grid> 
                )} */}
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <div style={GridStyles.addButtonContainer}>
            <CustomButton
              type="button"
              style={GridStyles.addButton}
              onClick={
                type?.toLocaleLowerCase() === "parentlookup" || isParent
                  ? handleLookupSave
                  : handleLookupValueSave
              }
            >
              <CheckCircleIcon style={GridStyles.iconStyle} /> Save
            </CustomButton>
          </div>
        </Grid>

        {type?.toLocaleLowerCase() === "parentlookup" || isParent ? (
          <Grid item xs={12} sm={6} md={12}>
            <Grid
              container
              spacing={2}
              sx={{ flexDirection: { xs: "column", sm: "row" } }}
            >
              <Grid item xs={12} sm={6} md={6}>
                <CustomTextField
                  label="Name"
                  name="name"
                  type="text"
                  value={lookupData.name}
                  onChange={(e) =>
                    setLookupData((prevData) => ({
                      ...prevData,
                      name: e.target.value,
                    }))
                  }
                  required={true}
                  error={touched.name && lookupData.name.trim() === ""}
                  helperText={
                    touched.name && lookupData.name.trim() === ""
                      ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                      : ""
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6} md={6}>
                <CustomTextField
                  label="Description"
                  name="description"
                  type="text"
                  value={lookupData.description}
                  onChange={(e) =>
                    setLookupData((prevData) => ({
                      ...prevData,
                      description: e.target.value,
                    }))
                  }
                  required={true}
                  error={
                    touched.description && lookupData.description.trim() === ""
                  }
                  helperText={
                    touched.description && lookupData.description.trim() === ""
                      ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                      : ""
                  }
                />
              </Grid>
            </Grid>
          </Grid>
        ) : (
          <Grid item xs={12} sm={6} md={12}>
            <Grid
              container
              spacing={2}
              sx={{ flexDirection: { xs: "column", sm: "row" } }}
            >
              <Grid item xs={12} sm={6} md={6}>
                <CustomSelect
                  label="Parent"
                  name="lookupId"
                  type="text"
                  SelectProps={{
                    children: Array.isArray(parentList.data) ? (
                      parentList.data.map((item: any) => (
                        <MenuItem key={item.id} value={item.id}>
                          {item.name}
                        </MenuItem>
                      ))
                    ) : parentList.data instanceof Object ? (
                      <MenuItem
                        key={(parentList.data as any).id}
                        value={(parentList.data as any).id}
                      >
                        {(parentList.data as any).name}
                      </MenuItem>
                    ) : null,

                    IconComponent: () => null,
                    renderValue: (value: any) => {
                      if (Array.isArray(parentList.data)) {
                        const selected = parentList.data.filter((item: any) =>
                          value.includes(item.id)
                        );
                        return selected.map((item) => item.name).join(", ");
                      } else if (parentList.data instanceof Object) {
                        return (parentList.data as any).name || "";
                      }
                      return "";
                    },
                  }}
                  value={lookupValueData.lookupId || ""}
                  onChange={(e) =>
                    setLookupValueData((prevData) => ({
                      ...prevData,
                      lookupId: e.target.value as number,
                    }))
                  }
                  multiple={false}
                  required={true}
                  disabled={id ? true : parentId ? true : false}
                  error={touched.parent && lookupValueData.lookupId === null}
                  helperText={
                    touched.parent && lookupValueData.lookupId === null
                      ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                      : ""
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6} md={6}>
                <CustomTextField
                  label="Description"
                  name="description"
                  type="text"
                  value={lookupValueData.description}
                  onChange={(e) =>
                    setLookupValueData((prevData) => ({
                      ...prevData,
                      description: e.target.value,
                    }))
                  }
                  required={true}
                  error={
                    touched.description &&
                    lookupValueData.description.trim() === ""
                  }
                  helperText={
                    touched.description &&
                    lookupValueData.description.trim() === ""
                      ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                      : ""
                  }
                />
              </Grid>
            </Grid>
            <Grid
              container
              spacing={2}
              sx={{ flexDirection: { xs: "column", sm: "row" } }}
            >
              <Grid item xs={12} sm={6} md={4}>
                <CustomTextField
                  label="Value (En)"
                  name="valueEn"
                  type="text"
                  value={lookupValueData.valueEn}
                  onChange={(e) =>
                    setLookupValueData((prevData) => ({
                      ...prevData,
                      valueEn: e.target.value,
                    }))
                  }
                  required={true}
                  error={
                    touched.valueEn && lookupValueData.valueEn.trim() === ""
                  }
                  helperText={
                    touched.valueEn && lookupValueData.valueEn.trim() === ""
                      ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                      : ""
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <CustomTextField
                  label="Value (Ar)"
                  name="valueAr"
                  type="text"
                  value={lookupValueData.valueAr}
                  onChange={(e) =>
                    setLookupValueData((prevData) => ({
                      ...prevData,
                      valueAr: e.target.value,
                    }))
                  }
                  required={true}
                  error={
                    touched.valueAr && lookupValueData.valueAr.trim() === ""
                  }
                  helperText={
                    touched.valueAr && lookupValueData.valueAr.trim() === ""
                      ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                      : ""
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <CustomTextField
                  label="Order"
                  name="ordering"
                  type="text"
                  value={lookupValueData.ordering}
                  onChange={(e: any) => {
                    const { name, value } = e.target;
                    if (!isNaN(Number(value))) {
                      setLookupValueData((prevData) => ({
                        ...prevData,
                        ordering: Number(value),
                      }));
                    } else {
                      setTouched((prevData) => ({
                        ...prevData,
                        ordering: true,
                      }));
                    }
                  }}
                  required={true}
                  error={
                    touched.ordering &&
                    (lookupValueData.ordering < minimumOrder ||
                      (lookupValueData.ordering !== null &&
                        isNaN(Number(lookupValueData.ordering))))
                  }
                  helperText={
                    touched.ordering &&
                    (lookupValueData.ordering < minimumOrder ||
                      (lookupValueData.ordering !== null &&
                        isNaN(Number(lookupValueData.ordering))))
                      ? `${VALIDATION_ERROR_INPUT_ORDER_MESSAGE}`
                      : ""
                  }
                />
              </Grid>
            </Grid>
          </Grid>
        )}
      </Grid>
    </form>
  );
};

export default LookupDetailsForm;
